import { Injectable, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { fieldsValidators } from '@shared/constants/field-name-validators';
import { Mapper } from '@shared/helpers/mapper';
import { QueryCollection } from '@shared/models/query-collection';
import { HeaderService } from '@shared/services';
import { dateRangeValidator } from '@shared/validators/date-range-validator';
import { notEmptyObjectValidator } from '@shared/validators/not-empty-object';
import moment from 'moment';
import { SecurityQuestionAnswerAuditableResponse } from 'src/app/web-api-client';
import { SecurityType } from '../../admin/models';
import { InvestmentJurisdictionShort } from '../../admin/models/investment-jurisdiction-short';
import { SecurityForm } from '../abstractions/security-form';
import { Entity } from '../models/entity';
import { EvaluationQuestion } from '../models/evaluation-question';
import { EvaluationTaxResult } from '../models/evaluation-tax-result';
import { RulesForm } from '../models/rules-form';
import { SecurityDetails } from '../models/security-details';
import { RulesFormManager } from './rules-form-manager';
import { SecurityService } from './security.service';

@Injectable()
export class SecurityFormGroupDefinition {
  private readonly formBuilder = inject(FormBuilder);
  private readonly securityService = inject(SecurityService);
  private readonly headerService = inject(HeaderService);
  private readonly ruleFormManager = inject(RulesFormManager);

  securityForm!: FormGroup;
  initialFormValue: SecurityForm | null = null;

  constructor() {
    this.init();
  }

  get acquisitionDate(): AbstractControl<Date[] | undefined> {
    return this.securityForm.get('acquisitionDate')! as AbstractControl<
      Date[] | undefined
    >;
  }

  get acquisitionDateValue(): Date {
    return this.securityForm.get('acquisitionDate')!.value[0];
  }

  get securityType(): AbstractControl<SecurityType | null | undefined> {
    return this.securityForm.get('securityType')!;
  }

  get securityTypeValue(): SecurityType | null | undefined {
    return this.securityForm.get('securityType')?.value;
  }

  get investmentJurisdiction(): AbstractControl<
    InvestmentJurisdictionShort | null | undefined
  > {
    return this.securityForm.get('investmentJurisdiction')!;
  }

  get investmentJurisdictionValue():
    | InvestmentJurisdictionShort
    | null
    | undefined {
    return this.securityForm.get('investmentJurisdiction')?.value;
  }

  get entity(): AbstractControl<Entity | null | undefined> {
    return this.securityForm.get('entity')!;
  }

  get entityValue(): Entity | null | undefined {
    return this.securityForm.get('entity')?.value;
  }

  get entityTypeName(): AbstractControl<string> {
    return this.securityForm.get('entityTypeName')!;
  }

  get entityTypeNameValue(): string | null | undefined {
    return this.securityForm.get('entityTypeName')?.value;
  }

  get residencyJurisdictionName(): AbstractControl<string> {
    return this.securityForm.get('residencyJurisdictionName')!;
  }

  get residencyJurisdictionNameValue(): string | null | undefined {
    return this.securityForm.get('residencyJurisdictionName')?.value;
  }

  get nameValue(): string | null | undefined {
    return this.securityForm.get('name')?.value;
  }

  get name(): AbstractControl<string> {
    return this.securityForm.get('name')!;
  }

  get isinCusipValue(): string | null | undefined {
    return this.securityForm.get('isinCusip')?.value;
  }

  get isinCusip(): AbstractControl<string> {
    return this.securityForm.get('isinCusip')!;
  }

  get valid(): boolean {
    return this.securityForm.valid;
  }

  get questions(): EvaluationQuestion[] {
    return this.ruleFormManager.allQuestions;
  }

  get taxResults(): EvaluationTaxResult[] {
    return this.ruleFormManager.allTaxResult;
  }

  loadSecurityInfo(securityId: number): void {
    this.securityService
      .getSecurityById(securityId)
      .subscribe((securityDetails: SecurityDetails) => {
        this.securityForm.patchValue(
          this.generateEditedModeValue(securityDetails)
        );
        this.updateRuleManager(securityDetails);
        this.automaticClientSelection(
          securityDetails.clientId ?? securityDetails.entity?.clientId
        );
        this.initialFormValue = this.securityForm.value;
      });
  }

  clearRuleForm(): void {
    this.ruleFormManager.clear();
  }

  reset(): void {
    this.init();
    this.initialFormValue = null;
  }

  private init(): void {
    this.securityForm = this.formBuilder.group({
      entity: [
        {} as Entity | null | undefined,
        [notEmptyObjectValidator(), Validators.required],
      ],
      generalRateEntityTypeId: [null as number | null],
      residencyJurisdictionName: [{ value: '', disabled: true }, []],
      entityTypeName: [{ value: '', disabled: true }, []],
      name: [
        '',
        [
          Validators.maxLength(255),
          Validators.pattern(fieldsValidators.letterRequired),
        ],
      ],
      securityType: [
        {} as SecurityType | null | undefined,
        [notEmptyObjectValidator(), Validators.required],
      ],
      investmentJurisdiction: [
        {} as InvestmentJurisdictionShort | null | undefined,
        [notEmptyObjectValidator(), Validators.required],
      ],
      isinCusip: [
        '',
        [
          Validators.maxLength(12),
          Validators.minLength(9),
          Validators.pattern(fieldsValidators.alphanumeric),
        ],
      ],
      acquisitionDate: [[moment().toDate()] as Date[], dateRangeValidator()],
    });
  }

  private generateEditedModeValue(
    securityDetails: SecurityDetails
  ): SecurityForm {
    return {
      entity: securityDetails.entity,
      clientId: securityDetails.clientId,
      generalRateEntityTypeId: securityDetails.generalRateEntityTypeId,
      residencyJurisdictionName:
        securityDetails.entity?.residencyJurisdiction?.name,
      entityTypeName: securityDetails.entity?.entityType?.name,
      name: securityDetails?.name ?? '',
      isinCusip: securityDetails?.isinCusip ?? '',
      investmentJurisdiction: securityDetails.investmentJurisdiction,
      securityType: securityDetails.securityType,
      acquisitionDate: securityDetails.acquisitionDate
        ? [securityDetails.acquisitionDate, securityDetails.acquisitionDate]
        : [],
      transactionType: securityDetails.transactionType?.transactionTypeId,
    } as SecurityForm;
  }

  private updateRuleManager(securityDetails: SecurityDetails): void {
    const ruleForm = new RulesForm();
    const questions: EvaluationQuestion[] = Mapper.mapByList(
      securityDetails.questions ?? [],
      EvaluationQuestion,
      [
        'questionId',
        'taxPolicyId',
        'taxRuleId',
        'securityQuestionAnswerId',
        'isActive',
        {
          answer: (source: SecurityQuestionAnswerAuditableResponse) =>
            source.text,
        },
      ]
    );
    const questionsQueryCollection = new QueryCollection<EvaluationQuestion>(
      questions
    );
    ruleForm.questions = questionsQueryCollection;
    const taxResults: EvaluationTaxResult[] = Mapper.mapByList(
      securityDetails.taxResults ?? [],
      EvaluationTaxResult,
      [
        'description',
        'rate',
        'taxResultItemTypeId',
        'securityTaxResultItemId',
        'taxPolicyId',
        'taxRuleId',
        'taxResultItemId',
      ]
    );

    const taxResultsQueryCollection = new QueryCollection<EvaluationTaxResult>(
      taxResults
    );
    ruleForm.taxResults = taxResultsQueryCollection;

    this.ruleFormManager.push(ruleForm);
  }

  loadSecurityData(securityDetails: SecurityDetails): void {
    this.securityForm.patchValue(this.generateEditedModeValue(securityDetails));
    this.updateRuleManager(securityDetails);
  }

  private automaticClientSelection(clientId?: number): void {
    if (!this.headerService.selectedClient()) {
      this.headerService.updatedClient.set(true);
      this.headerService.setClientById({ clientId, isEmitPrevention: true });
    }
  }
}
